/* eslint-disable max-lines */
import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useOutletContext } from 'react-router-dom';
import maplibregl from 'maplibre-gl';
import { MapboxOverlay } from '@deck.gl/mapbox';
import { PolygonLayer, TextLayer } from '@deck.gl/layers';

import {
  useAnswersForQuestionId,
  useParticipantAnswersForQuestionId,
} from '@services/query.service';
import {
  FLANDERS_BRUSSELS_REGION_EXTERIOR_RINGS,
} from '@App/components/Map/data/flanders-brussels-region';
import countryLabels from '@App/components/Map/data/country-labels';
import questionsDe from '../../../../locale/questions.de.json';
import questionsEn from '../../../../locale/questions.en.json';
import questionsFr from '../../../../locale/questions.fr.json';
import questionsNl from '../../../../locale/questions.nl.json';

import 'maplibre-gl/dist/maplibre-gl.css';
import './map.css';

const MAP_CENTER = [4.35, 51.05];
const FLANDERS_BOUNDS = [
  [2.5, 50.65],
  [5.9, 51.55],
];
const WORLD_MASK_RING = [
  [-180, -85],
  [180, -85],
  [180, 85],
  [-180, 85],
  [-180, -85],
];
const MAP_PADDING = 200;
const FOCUS_PADDING = {
  bottom: MAP_PADDING,
  left: MAP_PADDING,
  right: MAP_PADDING,
  top: MAP_PADDING * 2,
};
const DEFAULT_MAP_PITCH = 45;
const PRESERVED_MAPS = new WeakMap();
const LEGEND_TRANSLATIONS = {
  de: questionsDe,
  en: questionsEn,
  fr: questionsFr,
  nl: questionsNl,
};

function getLegendTranslation(lang, key, fallback = null) {
  if (!key) return fallback;

  const translations = LEGEND_TRANSLATIONS[lang] || LEGEND_TRANSLATIONS.nl;

  return translations[key] || fallback || key;
}

function animateToCamera(map, camera, options = {}, onComplete) {
  const { duration = 0, essential = true } = options;
  const startBearing = map.getBearing();
  const startPitch = map.getPitch();
  const targetCenter = Array.isArray(camera.center)
    ? { lng: camera.center[0], lat: camera.center[1] }
    : camera.center;
  if (!targetCenter
    || !Number.isFinite(targetCenter.lng)
    || !Number.isFinite(targetCenter.lat)
    || !Number.isFinite(camera.zoom)) return undefined;
  const targetBearing = camera.bearing ?? startBearing;
  const targetPitch = camera.pitch ?? startPitch;
  const handleMoveEnd = () => {
    map.off('moveend', handleMoveEnd);
    onComplete?.();
  };

  if (onComplete) map.once('moveend', handleMoveEnd);
  map.easeTo({
    bearing: targetBearing,
    center: targetCenter,
    essential,
    pitch: targetPitch,
    zoom: camera.zoom,
    duration,
  });

  return () => {
    map.off('moveend', handleMoveEnd);
    map.stop();
  };
}
function easeToBounds(map, bounds, options = {}) {
  const { onComplete, ...cameraOptions } = options;
  const {
    duration = 0, essential = true, pitch, ...boundsOptions
  } = cameraOptions;
  const camera = map.cameraForBounds(bounds, boundsOptions);

  if (!camera) return undefined;

  return animateToCamera(map, { ...camera, pitch }, { duration, essential }, onComplete);
}
function fitMapToFlanders(map, pitch, options = {}) {
  return easeToBounds(map, FLANDERS_BOUNDS, {
    padding: MAP_PADDING,
    pitch,
    ...options,
  });
}
function getRegionMaskLayer() {
  return new PolygonLayer({
    id: 'flanders-brussels-region-mask',
    data: [{ polygon: [WORLD_MASK_RING, ...FLANDERS_BRUSSELS_REGION_EXTERIOR_RINGS] }],
    getFillColor: [255, 255, 255, 10],
    getPolygon: item => item.polygon,
    pickable: false,
  });
}
function getCountryLabelsLayer(lang = 'nl') {
  return new TextLayer({
    billboard: true,
    id: 'country-labels',
    data: countryLabels[lang] || countryLabels.nl,
    getColor: [214, 232, 224, 185],
    getPosition: item => item.coordinates,
    getSize: 14,
    getText: item => item.name.toUpperCase(),
    getTextAnchor: 'middle',
    getAlignmentBaseline: 'center',
    outlineColor: [20, 31, 46, 220],
    outlineWidth: 2,
    parameters: { depthTest: false },
    pickable: false,
  });
}
function MapLegend({
  lang = 'nl',
  label = null,
  labelKey = null,
  gradient,
  minLabel = null,
  minLabelKey = 'legend-low',
  maxLabel = null,
  maxLabelKey = 'legend-high',
  scaleStyle = {},
  circleLabel = null,
  circleLabelKey = null,
  circleItems = [],
}) {
  const resolvedLabel = getLegendTranslation(lang, labelKey, label);
  const resolvedMinLabel = getLegendTranslation(lang, minLabelKey, minLabel);
  const resolvedMaxLabel = getLegendTranslation(lang, maxLabelKey, maxLabel);
  const resolvedCircleLabel = getLegendTranslation(lang, circleLabelKey, circleLabel);
  const resolvedCircleItems = [
    resolvedCircleLabel ? { label: resolvedCircleLabel } : null,
    ...circleItems.map(item => ({
      color: item.color,
      label: getLegendTranslation(lang, item.labelKey, item.label),
    })),
  ].filter(item => item?.label);

  return (
    <aside className="map__legend">
      <strong>{resolvedLabel}</strong>
      <div
        className="map__legend__scale"
        style={{ background: gradient, ...scaleStyle }}
      />
      <div className="map__legend__labels">
        <span>{resolvedMinLabel}</span>
        <span>{resolvedMaxLabel}</span>
      </div>
      {resolvedCircleItems.map(item => (
        <div className="map__legend__item" key={item.label}>
          <span className="map__legend__circle" style={{ background: item.color }} />
          <span>{item.label}</span>
        </div>
      ))}
    </aside>
  );
}
function Map({
  questionId,
  buildData,
  getLayers,
  layerOptions,
  legend = null,
  lastAnswer: lastAnswerProp,
  mapOptions = {},
  mapOpacity = 1,
  useParticipantAnswers = false,
  fitOnResize = false,
  autoRotate = false,
  focusCoordinates = null,
  focusBounds = null,
  focusZoom = 12,
  returnToOverview = false,
  revealDataAfterFocus = false,
  hideMap = false,
  mapContainerRef = null,
  tabletLang = 'nl',
  children = null,
  className = '',
}) {
  const outletContext = useOutletContext() || {};
  const resolvedMapContainerRef = mapContainerRef || outletContext.mapContainerRef;
  const lastAnswer = lastAnswerProp ?? outletContext.lastAnswer;
  const pitch = mapOptions.pitch ?? DEFAULT_MAP_PITCH;
  const hasAnswer = lastAnswer?.questionId === questionId && Boolean(
    lastAnswer.answer?.value
      || lastAnswer.answer?.label
      || lastAnswer.answer?.coordinates,
  );
  const { data: answerCount = {} } = useAnswersForQuestionId(questionId);
  const { data: participantAnswers = [] } = useParticipantAnswersForQuestionId(
    questionId,
    useParticipantAnswers,
  );
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [deckOverlay, setDeckOverlay] = useState(null);
  const [showAllData, setShowAllData] = useState(
    () => !revealDataAfterFocus || (!focusCoordinates && !focusBounds),
  );
  const hasFocusTarget = Boolean(focusCoordinates || focusBounds);
  const focusTargetKey = JSON.stringify([focusCoordinates, focusBounds]);
  const [completedFocusTarget, setCompletedFocusTarget] = useState(null);
  const visibleAllData = revealDataAfterFocus && hasFocusTarget
    ? completedFocusTarget === focusTargetKey
    : showAllData;

  const data = useMemo(
    () => buildData(answerCount, lastAnswer, participantAnswers, { showAllData: visibleAllData }),
    [answerCount, buildData, lastAnswer, participantAnswers, visibleAllData],
  );

  useEffect(() => {
    if (!revealDataAfterFocus) return;

    setShowAllData(!focusCoordinates && !focusBounds);
  }, [focusBounds, focusCoordinates, revealDataAfterFocus]);

  useEffect(() => {
    const container = resolvedMapContainerRef?.current || mapRef.current;
    if (!container) return undefined;

    const preservedMap = resolvedMapContainerRef ? PRESERVED_MAPS.get(container) : null;
    const newMap = preservedMap?.map || new maplibregl.Map({
      container,
      style: '/styles/dark/style.json',
      attributionControl: false,
      interactive: false,
      center: MAP_CENTER,
      pitch,
      zoom: 8,
    });
    const overlay = preservedMap?.overlay || new MapboxOverlay({ layers: [] });

    if (!preservedMap) {
      newMap.addControl(overlay);
      newMap.addControl(new maplibregl.AttributionControl({
        customAttribution: '© Nazka Mapps © OpenStreetMap contributors',
      }));
    }
    const resizeObserver = new ResizeObserver(() => {
      newMap.resize();
    });
    const handleMapLoad = () => setMapReady(true);

    if (newMap.isStyleLoaded()) {
      setMapReady(true);
    } else {
      newMap.once('load', handleMapLoad);
    }
    resizeObserver.observe(container);
    if (resolvedMapContainerRef) {
      PRESERVED_MAPS.set(container, { map: newMap, overlay });
    }
    setMap(newMap);
    setDeckOverlay(overlay);

    return () => {
      resizeObserver.disconnect();
      newMap.off('load', handleMapLoad);
      setMapReady(false);
      if (resolvedMapContainerRef) overlay.setProps({ layers: [] });
      if (!resolvedMapContainerRef) newMap.remove();
    };
  }, [pitch, resolvedMapContainerRef]);

  useEffect(() => {
    if (!map || !mapReady || focusCoordinates || focusBounds) return undefined;

    return fitMapToFlanders(map, pitch);
  }, [focusBounds, focusCoordinates, map, mapReady, pitch]);

  useEffect(() => {
    if (!map || !mapReady || !fitOnResize) return undefined;

    const container = resolvedMapContainerRef?.current || mapRef.current;
    if (!container) return undefined;

    let resizeFrame;
    let cancelFit;
    let previousSize;
    const resizeObserver = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (previousSize?.width === width && previousSize?.height === height) return;
      const isInitialMeasurement = !previousSize;
      previousSize = { width, height };
      map.resize();
      if (isInitialMeasurement) return;
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => {
        cancelFit?.();
        cancelFit = fitMapToFlanders(map, pitch);
      });
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      cancelAnimationFrame(resizeFrame);
      cancelFit?.();
    };
  }, [fitOnResize, map, mapReady, pitch, resolvedMapContainerRef]);

  useEffect(() => {
    if (!map || !mapReady || !deckOverlay) return;

    deckOverlay.setProps({
      layers: [
        getRegionMaskLayer(),
        getCountryLabelsLayer(tabletLang),
        ...getLayers({
          data,
          lastAnswer,
          options: { ...layerOptions, showAllData: visibleAllData },
        }),
      ],
    });
  }, [
    data,
    deckOverlay,
    getLayers,
    lastAnswer,
    layerOptions,
    map,
    mapReady,
    visibleAllData,
    tabletLang,
  ]);

  useEffect(() => {
    if (!map || !mapReady || !autoRotate || focusCoordinates || focusBounds || hasAnswer) {
      return undefined;
    }

    const rotationTimer = window.setInterval(() => {
      map.setBearing(map.getBearing() + 0.175);
    }, 30);

    return () => {
      window.clearInterval(rotationTimer);
    };
  }, [autoRotate, focusBounds, focusCoordinates, hasAnswer, map, mapReady]);

  useEffect(() => {
    if (!map || !mapReady || !hasAnswer) return;

    map.stop();
    map.setBearing(0);
  }, [hasAnswer, map, mapReady]);

  useEffect(() => {
    if (!map || !mapReady || (!focusCoordinates && !focusBounds)) return undefined;

    const revealAllData = () => {
      setCompletedFocusTarget(focusTargetKey);
      setShowAllData(true);
    };
    let cancelCameraAnimation;

    const returnToOverviewMap = () => {
      map.stop();
      map.setBearing(0);
      cancelCameraAnimation = fitMapToFlanders(map, pitch, {
        duration: 1800,
        onComplete: revealDataAfterFocus ? revealAllData : undefined,
      });
    };

    const focusMap = () => {
      cancelCameraAnimation?.();
      map.stop();
      map.setBearing(0);
      if (focusBounds) {
        cancelCameraAnimation = easeToBounds(map, focusBounds, {
          duration: 3600,
          essential: true,
          maxZoom: 12,
          padding: FOCUS_PADDING,
          pitch,
          onComplete: returnToOverview ? returnToOverviewMap : undefined,
        });
      } else {
        cancelCameraAnimation = animateToCamera(map, {
          center: focusCoordinates,
          zoom: focusZoom,
          pitch,
        }, { duration: 2400, essential: true }, returnToOverview ? returnToOverviewMap : undefined);
      }
    };

    focusMap();

    return () => {
      cancelCameraAnimation?.();
    };
  }, [
    focusBounds,
    focusCoordinates,
    focusTargetKey,
    focusZoom,
    map,
    mapReady,
    pitch,
    revealDataAfterFocus,
    returnToOverview,
  ]);

  return (
    <main className={`map ${className}`.trim()}>
      {children}
      {!resolvedMapContainerRef && (
        <div
          ref={mapRef}
          className={`map__canvas${hideMap ? ' map__canvas--hidden' : ''}`}
          style={{ opacity: mapOpacity }}
        />
      )}
      {legend}
    </main>
  );
}

export default Map;

export { MapLegend };